/**
 * Everything the pre-declaration panel derived in the browser, vendored here so the
 * declared panel's own modules stay free of it. Deleted with the flag.
 */
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import {
  Reconciliation,
  ReconciliationOrigin,
} from '@/reducers/admin/financialAccountsReducer'
import { BookkeepingReport } from '@/reducers/finances/bookkeepingReportsReducer'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

const sortByEndDateDesc = (reconciliations: Reconciliation[]) =>
  orderBy(reconciliations, 'endingBalanceDate', 'desc')

/**
 * A report has at most one reconciliation, so there is nothing to prefer between.
 * `null` is the ordinary unreconciled month, not a missing row: a month nobody has
 * reconciled, or one sitting under a spanning catch-up recon, has none of its own.
 */
export const findCurrentReconciliation = (
  bookkeepingReport: BookkeepingReport | undefined,
  accountReconciliations: Reconciliation[]
) => {
  if (!bookkeepingReport) return null

  return (
    accountReconciliations.find(
      (recon) => recon.bookkeepingReportId === bookkeepingReport.id
    ) ?? null
  )
}

const findPreviousReconciliation = (
  bookkeepingReport: BookkeepingReport | undefined,
  accountReconciliations: Reconciliation[]
) => {
  if (!bookkeepingReport) return null

  const previousRecons = sortByEndDateDesc(accountReconciliations).filter(
    (recon) => recon.endingBalanceDate < bookkeepingReport.startDate
  )

  return previousRecons.length > 0 ? previousRecons[0] : null
}

/**
 * The bookkeeping month a reconciliation belongs to: the month its period closes in.
 * Mirrors `reportMonthForClose` in the API's `reconciliation_core.ts`, which is the
 * source of truth.
 */
const reportMonthForClose = (endingBalanceDate: string) =>
  DateTime.fromISO(endingBalanceDate, { zone: 'utc' }).toFormat(
    DATE_FORMATS_LUXON.YEAR_MONTH
  )

type ReconciliationPeriod = {
  startingBalanceDate: string
  endingBalanceDate: string
}

type ReportPeriod = { date: string; startDate: string; endDate: string }

const closesInReportMonth = (
  reconciliation: ReconciliationPeriod,
  report: ReportPeriod
) => reportMonthForClose(reconciliation.endingBalanceDate) === report.date

/**
 * CUBK auto reconciliations legitimately span many months while being attached to a
 * single report, so the months in between are linked by nothing but this containment.
 * Without it they read as unreconciled and invite a duplicate.
 */
const spansReportPeriod = (
  reconciliation: ReconciliationPeriod,
  report: ReportPeriod
) =>
  report.startDate >= reconciliation.startingBalanceDate &&
  report.endDate <= reconciliation.endingBalanceDate

const findReconciliationSpanningBKPeriod = (
  bookkeepingReport: BookkeepingReport | undefined,
  accountReconciliations: Reconciliation[]
): Reconciliation | null => {
  if (!bookkeepingReport) return null

  const sorted = sortByEndDateDesc(accountReconciliations)

  // Close-month first: a reconciliation that closes here owns the month outright, and
  // sorting by close date alone would let a longer spanning recon outrank it.
  return (
    sorted.find((recon) => closesInReportMonth(recon, bookkeepingReport)) ??
    sorted.find((recon) => spansReportPeriod(recon, bookkeepingReport)) ??
    null
  )
}

/**
 * The one answer to "which reconciliation is this report's?". The monthly-books panel and
 * the reconciliation modal must both use it, or a second reconciliation gets created for
 * a period already covered.
 */
export const resolveReconciliationForReport = (
  bookkeepingReport: BookkeepingReport | undefined,
  accountReconciliations: Reconciliation[]
): Reconciliation | null =>
  findCurrentReconciliation(bookkeepingReport, accountReconciliations) ??
  findReconciliationSpanningBKPeriod(bookkeepingReport, accountReconciliations)

const utcMillis = (iso: string) =>
  DateTime.fromISO(iso, { zone: 'utc' }).toMillis()

/**
 * The API's `isInvertedPeriod`. Day granularity keeps rows that invert only by hours
 * usable as anchors.
 */
const isInvertedPeriod = (reconciliation: ReconciliationPeriod) =>
  DateTime.fromISO(reconciliation.startingBalanceDate, {
    zone: 'utc',
  }).startOf('day') >
  DateTime.fromISO(reconciliation.endingBalanceDate, { zone: 'utc' }).startOf(
    'day'
  )

/**
 * Whether auto-recon has an ending balance to chain this report from. Mirrors the API's
 * anchor search: `type` is unfiltered because an `auto` predecessor is a valid anchor, and
 * the report's own rows are excluded because an inverted row ends before its own report
 * starts and would otherwise anchor itself.
 *
 * The lower bound is the API's one-cycle guard. Testing every row against the window
 * answers the same question the API asks of the latest one: no row inside it means the
 * latest is outside it too.
 */
export const hasAutoReconAnchor = (
  bookkeepingReport: BookkeepingReport,
  accountReconciliations: Reconciliation[]
) => {
  const reportStart = DateTime.fromISO(bookkeepingReport.startDate, {
    zone: 'utc',
  })
  const oneCycleBack = reportStart.minus({ months: 1 }).toMillis()

  return accountReconciliations.some(
    (recon) =>
      recon.status === 'complete' &&
      recon.bookkeepingReportId !== bookkeepingReport.id &&
      utcMillis(recon.endingBalanceDate) < reportStart.toMillis() &&
      utcMillis(recon.endingBalanceDate) >= oneCycleBack &&
      !isInvertedPeriod(recon)
  )
}

/**
 * The predecessor the API's recover path resolves. Not `findPreviousReconciliation`, which
 * keys on the report's start date instead of the reconciliation's own: the two disagree on
 * any period starting mid-month, and offering recover against a row the server resolves
 * differently is a button that can only fail.
 */
const findRecoverPredecessor = (
  reconciliation: Reconciliation,
  accountReconciliations: Reconciliation[]
) => {
  const priorCandidates = orderBy(
    accountReconciliations.filter(
      (recon) =>
        // An inverted row closes before it starts, so it qualifies as its own predecessor.
        recon.id !== reconciliation.id &&
        utcMillis(recon.endingBalanceDate) <
          utcMillis(reconciliation.startingBalanceDate)
    ),
    [(recon) => utcMillis(recon.endingBalanceDate), 'id'],
    ['desc', 'desc']
  )

  const [nearest] = priorCandidates
  if (!nearest) return null

  return (
    (typeof nearest.bookkeepingReportId === 'number'
      ? priorCandidates.find(
          (recon) =>
            recon.bookkeepingReportId === nearest.bookkeepingReportId &&
            recon.status === 'complete'
        )
      : undefined) ?? nearest
  )
}

/**
 * Takes the report's already-resolved reconciliation rather than deriving it again, so
 * the panel and this helper cannot disagree about which row the month owns.
 */
export const getRecoverabilityMetadata = (
  bookkeepingReport: BookkeepingReport | undefined,
  accountReconciliations: Reconciliation[],
  reconciliation: Reconciliation | null
) => {
  const previousReconciliation = reconciliation
    ? findRecoverPredecessor(reconciliation, accountReconciliations)
    : findPreviousReconciliation(bookkeepingReport, accountReconciliations)

  let recoverable = false
  let recovering = false
  if (reconciliation?.status === 'incomplete') {
    // A row waiting on this one says so, whether or not it is the next by report. A
    // settled row keeps its pointer forever, so only an open one is still waiting.
    recovering = accountReconciliations.some(
      (sibling) =>
        sibling.blockedOnReconciliationId === reconciliation.id &&
        sibling.status !== 'complete'
    )
    // The server only reopens an auto-recon predecessor, so recover has nowhere to
    // go against any other origin, and it refuses outright once later months wait on
    // this one. Manual reconciliation is the route out of both.
    recoverable =
      !recovering &&
      previousReconciliation?.status === 'complete' &&
      previousReconciliation.origin === ReconciliationOrigin.autoRecon
  }

  return {
    reconciliation,
    recoverable,
    recovering,
    previousReconciliation,
  }
}

type BlockedState = Pick<Reconciliation, 'blockedOnReconciliationId'>

type ChainState = Pick<
  Reconciliation,
  'id' | 'status' | 'blockedOnReconciliationId'
>

/**
 * The server's `isReconciliationBlocked`, resolved against the account's other
 * reconciliations. An unloaded target reads as still waiting: greying a ready month is
 * recoverable, letting a bookkeeper reconcile a blocked one is not.
 */
const isReconciliationBlocked = (
  reconciliation: BlockedState,
  siblings: Pick<Reconciliation, 'id' | 'status'>[]
): boolean => {
  const blockedOnId = reconciliation.blockedOnReconciliationId
  if (blockedOnId === null || blockedOnId === undefined) return false
  const blockedOn = siblings.find((sibling) => sibling.id === blockedOnId)
  return blockedOn ? blockedOn.status !== 'complete' : true
}

/**
 * The status to render, with the pointer as the only authority on blocked. A row left on
 * the legacy stored `blocked` status reads as `incomplete`, so the column cannot outrank
 * the derivation and put a blocked card on screen that names no month.
 */
export const effectiveReconciliationStatus = (
  reconciliation: Pick<Reconciliation, 'status' | 'blockedOnReconciliationId'>,
  siblings: Pick<Reconciliation, 'id' | 'status'>[]
): Reconciliation['status'] => {
  if (isReconciliationBlocked(reconciliation, siblings)) return 'blocked'
  return reconciliation.status === 'blocked'
    ? 'incomplete'
    : reconciliation.status
}

/**
 * The reconciliation at the head of the chain, which is the only one a bookkeeper can act
 * on: the next hop back may be waiting on something itself. Null when nothing waited on is
 * loaded.
 *
 * Mirrors the server's `resolveEarliestOpenReconciliation`. Nothing forbids a cycle
 * between two rows, so the walk carries a visited set.
 */
export const resolveEarliestOpenReconciliation = <T extends ChainState>(
  reconciliation: ChainState,
  siblings: T[]
): T | null => {
  const visited = new Set<number>([reconciliation.id])
  let earliest: T | null = null
  let blockedOnId = reconciliation.blockedOnReconciliationId ?? null

  while (blockedOnId !== null && !visited.has(blockedOnId)) {
    const blockedOn = siblings.find((sibling) => sibling.id === blockedOnId)
    if (!blockedOn || blockedOn.status === 'complete') break
    visited.add(blockedOn.id)
    earliest = blockedOn
    blockedOnId = blockedOn.blockedOnReconciliationId ?? null
  }

  return earliest
}

export const FIRST_BALANCE_CHANGE_CAPTURED = DateTime.fromISO(
  '2024-07-30T20:14:04.960Z'
)

export const FIRST_ELIGIBLE_REPORTING_PERIOD = '2024-08'

export const PROBLEMATIC_INSTITUTIONS = [
  'ins_14', // TD Bank
  'ins_127296', // Bluevine
  'ins_115674', // FirstBank (Colorado)
  'ins_110438', // GreenState Credit Union
  'ins_116225', // Webster Bank
  'ins_120013', // America First Credit Union
  'ins_113059', // Logix Credit Union
]
